"use client";

import { logger } from "@utilities/logger";
import { useState, useCallback, useMemo } from "react";

// ============================================================================
// Types
// ============================================================================

/**
 * Configuration for a single step in the flow (used internally)
 */
export type StepConfig<TData, TStepId extends string = string> = {
  /** Unique identifier for the step */
  id: TStepId;
  /**
   * Optional function returning step index for progress display.
   * Receives the full flow state for dynamic computation.
   * Return `undefined` for steps that shouldn't be counted in progress.
   */
  stepIndex?: (state: FlowState<TData, TStepId>) => number | undefined;
  /**
   * Determines the next step based on current data.
   * Return `null` to mark the flow as complete.
   */
  next: (data: TData) => TStepId | null;
};

/**
 * Helper type to enforce that each step's `id` property matches its key in the steps object.
 * This provides type-safe step definitions with zero runtime transformation.
 */
type StepsWithMatchingIds<TData, TStepIds extends string> = {
  [K in TStepIds]: {
    /** Must match the key */
    id: K;
    /**
     * Optional function returning step index for progress display.
     * Receives the full flow state for dynamic computation.
     */
    stepIndex?: (state: FlowState<TData, TStepIds>) => number | undefined;
    /**
     * Determines the next step based on current data.
     * Return `null` to mark the flow as complete.
     */
    next: (data: TData) => TStepIds | null;
  };
};

/**
 * The shape returned by defineFlow
 */
export type FlowDefinition<TData, TStepId extends string = string> = {
  /** Initial data for the flow */
  initialData: TData;
  /** Map of step IDs to their configurations */
  steps: Record<TStepId, StepConfig<TData, TStepId>>;
  /**
   * Optional function returning total steps for progress display.
   * Receives the full flow state for dynamic computation.
   */
  totalSteps?: (state: FlowState<TData, TStepId>) => number;
  /** All valid step IDs (for type inference) */
  _stepIds: TStepId[];
};

/**
 * Optional configuration for useFlow
 */
export type UseFlowOptions<TStepId extends string> = {
  /** The step ID to start the flow with. Defaults to first step. */
  initialStep?: TStepId;
};

/**
 * State shape for the flow
 */
export type FlowState<TData, TStepId extends string = string> = {
  currentStepId: TStepId;
  data: TData;
  history: TStepId[];
  isComplete: boolean;
};

/**
 * Return type of the useFlow hook
 */
export type UseFlowReturn<TData, TStepId extends string = string> = {
  /** Current step ID */
  currentStepId: TStepId;
  /** Current step configuration */
  currentStep: StepConfig<TData, TStepId>;
  /** Accumulated flow data */
  data: TData;
  /** Navigation history (step IDs visited) */
  history: TStepId[];
  /** True when the flow has finished (next returned null) */
  isComplete: boolean;
  /** True if there's history to go back to */
  canGoBack: boolean;
  /** Current step's index for progress display (undefined if step has no stepIndex function or returns undefined) */
  stepIndex: number | undefined;
  /** Total steps for progress display (undefined if flow has no totalSteps function) */
  totalSteps: number | undefined;
  /**
   * Go to a specific step by ID.
   * This is useful for skipping steps or for manual navigation.
   */
  goToStep: (stepId: TStepId) => void;
  /**
   * Advance to the next step.
   * Optionally merge new data before transitioning.
   * Call this after validation passes in your component.
   */
  next: (stepData?: Partial<TData>) => void;
  /** Go back to the previous step */
  back: () => void;
  /** Update data without advancing to next step */
  setData: (partial: Partial<TData>) => void;
  /** Reset flow to initial state */
  reset: () => void;
};

// ============================================================================
// defineFlow Helper
// ============================================================================

/**
 * Creates a fully-typed flow configuration with automatic type inference.
 * Zero runtime overhead - this is purely a type helper that passes through the config as-is.
 *
 * @param config - Flow configuration object
 * @param config.data - The initial data state for the flow
 * @param config.steps - Object mapping step IDs to step definitions (id must match the key)
 * @returns A flow definition ready to be passed to useFlow
 *
 * @example
 * ```tsx
 * const agentFlow = defineFlow({
 *   data: { phone: "", otp: "", consent: false },
 *   totalSteps: () => 3,
 *   steps: {
 *     info: {
 *       id: "info",  // Must match the key
 *       next: () => "phone",
 *     },
 *     phone: {
 *       id: "phone",
 *       stepIndex: () => 1,
 *       next: (data) => (data.phone ? "otp" : null),
 *     },
 *     otp: {
 *       id: "otp",
 *       stepIndex: () => 2,
 *       next: (data) => (data.otp ? "consent" : null),
 *     },
 *     consent: {
 *       id: "consent",
 *       stepIndex: () => 3,
 *       next: () => null,
 *     },
 *   },
 * });
 *
 * // In component - no generics needed!
 * const flow = useFlow(agentFlow);
 * // Or start from a specific step:
 * const flow = useFlow(agentFlow, { initialStep: "phone" });
 *
 * flow.currentStepId; // type: "info" | "phone" | "otp" | "consent"
 * flow.data.phone;    // type: string
 * flow.stepIndex;     // type: number | undefined
 * flow.totalSteps;    // type: number | undefined
 * ```
 */
export function defineFlow<
  TData,
  const TSteps extends StepsWithMatchingIds<TData, Extract<keyof TSteps, string>>,
>(config: {
  data: TData;
  steps: TSteps;
  /**
   * Optional function returning total steps for progress display.
   * Receives the full flow state for dynamic computation.
   */
  totalSteps?: (state: FlowState<TData, Extract<keyof TSteps, string>>) => number;
}): FlowDefinition<TData, Extract<keyof TSteps, string>> {
  const stepIds = Object.keys(config.steps) as Extract<keyof TSteps, string>[];

  return {
    initialData: config.data,
    steps: config.steps as Record<Extract<keyof TSteps, string>, StepConfig<TData, Extract<keyof TSteps, string>>>,
    totalSteps: config.totalSteps,
    _stepIds: stepIds,
  };
}

// ============================================================================
// Hook
// ============================================================================

/**
 * A hook for managing multi-step user flows with branching and navigation history.
 *
 * @param flowDefinition - Flow definition created by defineFlow
 * @param options - Optional configuration (e.g., initialStep)
 * @returns Flow state and navigation functions
 *
 * @example
 * ```tsx
 * const myFlow = defineFlow({
 *   data: { choice: null as string | null },
 *   steps: {
 *     start: { id: "start", next: (d) => d.choice ? "end" : null },
 *     end: { id: "end", next: () => null },
 *   },
 * });
 *
 * // Start from first step (default)
 * const flow = useFlow(myFlow);
 *
 * // Or start from a specific step
 * const flow = useFlow(myFlow, { initialStep: "end" });
 *
 * // Navigate
 * flow.next({ choice: 'a' });
 * flow.back();
 *
 * // Update data without navigating
 * flow.setData({ choice: 'b' });
 * ```
 */
export function useFlow<TData, TStepId extends string>(
  flowDefinition: FlowDefinition<TData, TStepId>,
  options?: UseFlowOptions<TStepId>,
): UseFlowReturn<TData, TStepId> {
  const initialStep = options?.initialStep ?? flowDefinition._stepIds[0];

  const [state, setState] = useState<FlowState<TData, TStepId>>(() => ({
    currentStepId: initialStep,
    data: flowDefinition.initialData,
    history: [],
    isComplete: false,
  }));

  const currentStep = useMemo(
    () => flowDefinition.steps[state.currentStepId],
    [flowDefinition.steps, state.currentStepId],
  );

  const stepIndex = useMemo(() => currentStep.stepIndex?.(state), [currentStep, state]);

  const totalSteps = useMemo(() => flowDefinition.totalSteps?.(state), [flowDefinition.totalSteps, state]);

  const goToStep = useCallback((stepId: TStepId) => {
    setState(prev => ({
      ...prev,
      currentStepId: stepId,
      history: prev.currentStepId ? [...prev.history, prev.currentStepId] : prev.history,
    }));
  }, []);

  const next = useCallback(
    (stepData?: Partial<TData>) => {
      setState(prev => {
        // Don't navigate if already complete
        if (prev.isComplete) return prev;

        // Merge new data if provided
        const newData = stepData ? { ...prev.data, ...stepData } : prev.data;

        // Get the current step config and determine next step
        const currentStepConfig = flowDefinition.steps[prev.currentStepId];
        const nextStepId = currentStepConfig.next(newData);

        // If next returns null, flow is complete
        if (nextStepId === null) {
          return {
            ...prev,
            data: newData,
            isComplete: true,
          };
        }

        // Validate that the next step exists
        if (!flowDefinition.steps[nextStepId]) {
          logger.error(null, `useFlow: Step "${nextStepId}" does not exist in flowDefinition`);
          return prev;
        }

        return {
          ...prev,
          currentStepId: nextStepId,
          data: newData,
          history: [...prev.history, prev.currentStepId],
        };
      });
    },
    [flowDefinition.steps],
  );

  const back = useCallback(() => {
    setState(prev => {
      if (prev.history.length === 0) return prev;

      const newHistory = [...prev.history];
      const previousStepId = newHistory.pop()!;

      return {
        ...prev,
        currentStepId: previousStepId,
        history: newHistory,
        isComplete: false, // Allow re-navigating if we go back from complete
      };
    });
  }, []);

  const setData = useCallback((partial: Partial<TData>) => {
    setState(prev => ({
      ...prev,
      data: { ...prev.data, ...partial },
    }));
  }, []);

  const reset = useCallback(() => {
    setState({
      currentStepId: initialStep,
      data: flowDefinition.initialData,
      history: [],
      isComplete: false,
    });
  }, [initialStep, flowDefinition.initialData]);

  return {
    currentStepId: state.currentStepId,
    currentStep,
    data: state.data,
    history: state.history,
    isComplete: state.isComplete,
    canGoBack: state.history.length > 0,
    stepIndex,
    totalSteps,
    goToStep,
    next,
    back,
    setData,
    reset,
  };
}
